// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Trash2, RefreshCw, ShieldOff, Copy, Check } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Button,
  Card,
  Badge,
  Checkbox,
  EmptyState,
  DataTable,
  Skeleton,
  Input,
  Select,
  Modal,
  ConfirmDialog,
} from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import {
  useRewardCampaignDetail,
  useCreateRewardCampaign,
  useUpdateRewardCampaign,
  useDeleteRewardCampaign,
  usePublishRewardCampaign,
  usePartnerShops,
  useCreatePartnerShop,
  useUpdatePartnerShop,
  useDeletePartnerShop,
  useRevokePartnerShopSession,
  useRotatePartnerShopToken,
  useRewardCatalogItems,
  useCreateRewardCatalogItem,
  useUpdateRewardCatalogItem,
  useDeleteRewardCatalogItem,
  type PartnerShop,
  type RewardCatalogItem,
} from '@/hooks/useRewardCampaigns'
import { useProperties } from '@/hooks/useProperties'
import { useNotificationStore } from '@/stores'
import { formatBaht } from '@/lib/money'
import { useDateFormatter } from '@/hooks/useDateFormatter'

// ─── Schemas ──────────────────────────────────────────────────────────────────

const campaignSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  earn_model: z.enum(['stamp', 'points', 'instant']),
  status: z.enum(['draft', 'active', 'paused', 'closed']),
  property_id: z.coerce.number().optional().nullable(),
})

const publishSchema = z.object({
  funded_amount: z.coerce.number().min(0),
  subsidy_amount: z.coerce.number().min(0),
  starts_at: z.string().min(1),
  ends_at: z.string().min(1),
})

const shopSchema = z.object({
  name: z.string().min(1),
  contact_name: z.string().optional(),
  phone: z.string().optional(),
  tax_id: z.string().optional(),
  address: z.string().optional(),
})

const catalogItemSchema = z.object({
  name: z.string().min(1),
  reward_type: z.enum(['partner_voucher', 'rent_credit', 'flowrent_perk', 'instant_discount']),
  cost_points: z.coerce.number().min(0),
  thb_value: z.coerce.number().min(0),
  funding: z.string().min(1),
  stock: z.coerce.number().optional().nullable(),
  per_customer_limit: z.coerce.number().optional().nullable(),
  active: z.boolean().optional(),
})

type CampaignFormData = z.infer<typeof campaignSchema>
type PublishFormData = z.infer<typeof publishSchema>
type ShopFormData = z.infer<typeof shopSchema>
type CatalogItemFormData = z.infer<typeof catalogItemSchema>

// ─── Sub-component: Partner Shop Form Modal ───────────────────────────────────

function ShopFormModal({
  campaignId,
  shop,
  onClose,
}: {
  campaignId: number
  shop?: PartnerShop | null
  onClose: () => void
}) {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const createShop = useCreatePartnerShop()
  const updateShop = useUpdatePartnerShop()

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ShopFormData>({
    resolver: zodResolver(shopSchema) as unknown as Resolver<ShopFormData>,
    defaultValues: {
      name: shop?.name ?? '',
      contact_name: shop?.contact_name ?? '',
      phone: shop?.phone ?? '',
      tax_id: shop?.tax_id ?? '',
      address: shop?.address ?? '',
    },
  })

  const onSubmit = async (data: ShopFormData) => {
    if (shop) {
      await updateShop.mutateAsync({ id: shop.id, ...data })
      addToast({ type: 'success', message: t('partner_shops.form.update_success') })
    } else {
      await createShop.mutateAsync({ campaign_id: campaignId, ...data })
      addToast({ type: 'success', message: t('partner_shops.form.create_success') })
    }
    onClose()
  }

  return (
    <Modal isOpen onClose={onClose} title={t('partner_shops.new_button')}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label={t('partner_shops.fields.name')} {...register('name')} error={errors.name?.message} />
        <Input label={t('partner_shops.fields.contact_name')} {...register('contact_name')} />
        <Input label={t('partner_shops.fields.phone')} {...register('phone')} />
        <Input label={t('partner_shops.fields.tax_id')} {...register('tax_id')} />
        <Input label={t('partner_shops.fields.address')} {...register('address')} />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>{tc('actions.cancel')}</Button>
          <Button type="submit" loading={isSubmitting}>{t('partner_shops.form.save_button')}</Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Sub-component: Token Reveal Panel ────────────────────────────────────────

function TokenRevealPanel({ token, onClose }: { token: string; onClose: () => void }) {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Modal isOpen onClose={onClose} title={t('partner_shops.token_panel.title')}>
      <p className="text-sm text-base-content/70 mb-3">{t('partner_shops.token_panel.description')}</p>
      <div className="bg-base-200 rounded-lg p-3 flex items-center gap-2 font-mono text-sm break-all">
        <span className="flex-1">{token}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCopy}
          className="flex-shrink-0"
          aria-label={tc('labels.copy')}
        >
          {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={onClose}>{tc('actions.close')}</Button>
      </div>
    </Modal>
  )
}

// ─── Sub-component: Catalog Item Form Modal ───────────────────────────────────

function CatalogItemFormModal({
  campaignId,
  item,
  onClose,
}: {
  campaignId: number
  item?: RewardCatalogItem | null
  onClose: () => void
}) {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const createItem = useCreateRewardCatalogItem()
  const updateItem = useUpdateRewardCatalogItem()

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<CatalogItemFormData>({
    resolver: zodResolver(catalogItemSchema) as unknown as Resolver<CatalogItemFormData>,
    defaultValues: {
      name: item?.name ?? '',
      reward_type: item?.reward_type ?? 'partner_voucher',
      cost_points: item?.cost_points ?? 0,
      thb_value: item?.thb_value ?? 0,
      funding: item?.funding ?? '',
      stock: item?.stock ?? null,
      per_customer_limit: item?.per_customer_limit ?? null,
      active: item?.active ?? true,
    },
  })

  const rewardTypeOptions = [
    { value: 'partner_voucher', label: t('catalog.reward_type.partner_voucher') },
    { value: 'rent_credit', label: t('catalog.reward_type.rent_credit') },
    { value: 'flowrent_perk', label: t('catalog.reward_type.flowrent_perk') },
    { value: 'instant_discount', label: t('catalog.reward_type.instant_discount') },
  ]

  const onSubmit = async (data: CatalogItemFormData) => {
    if (item) {
      await updateItem.mutateAsync({ id: item.id, ...data })
      addToast({ type: 'success', message: t('catalog.form.update_success') })
    } else {
      await createItem.mutateAsync({ campaign_id: campaignId, ...data })
      addToast({ type: 'success', message: t('catalog.form.create_success') })
    }
    onClose()
  }

  return (
    <Modal isOpen onClose={onClose} title={t('catalog.new_button')}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label={t('catalog.fields.name')} {...register('name')} error={errors.name?.message} />
        <Select
          label={t('catalog.fields.reward_type')}
          options={rewardTypeOptions}
          {...register('reward_type')}
        />
        <Input
          label={t('catalog.fields.cost_points')}
          type="number"
          min={0}
          {...register('cost_points')}
          error={errors.cost_points?.message}
        />
        <Input
          label={t('catalog.fields.thb_value')}
          type="number"
          min={0}
          step="0.01"
          {...register('thb_value')}
          error={errors.thb_value?.message}
        />
        <Input label={t('catalog.fields.funding')} {...register('funding')} error={errors.funding?.message} />
        <Input label={t('catalog.fields.stock')} type="number" min={0} {...register('stock')} />
        <Input label={t('catalog.fields.per_customer_limit')} type="number" min={0} {...register('per_customer_limit')} />
        <Checkbox label={t('catalog.fields.active')} {...register('active')} />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>{tc('actions.cancel')}</Button>
          <Button type="submit" loading={isSubmitting}>{t('catalog.form.save_button')}</Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new'
  const campaignId = isNew ? 0 : Number(id)
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const addToast = useNotificationStore((s) => s.addToast)
  const formatDate = useDateFormatter()

  const { data: campaign, isLoading, isFetching } = useRewardCampaignDetail(campaignId)
  const { data: propertiesData } = useProperties()
  const properties = propertiesData?.data ?? []

  const createCampaign = useCreateRewardCampaign()
  const updateCampaign = useUpdateRewardCampaign()
  const deleteCampaign = useDeleteRewardCampaign()
  const publishCampaign = usePublishRewardCampaign()

  const { data: shopsData, isLoading: shopsLoading } = usePartnerShops(
    isNew ? {} : { campaign_id: campaignId },
  )
  const shops = shopsData?.data ?? []

  const { data: catalogData, isLoading: catalogLoading } = useRewardCatalogItems(
    isNew ? {} : { campaign_id: campaignId },
  )
  const catalogItems = catalogData?.data ?? []

  const revokeSession = useRevokePartnerShopSession()
  const rotateToken = useRotatePartnerShopToken()
  const deleteShop = useDeletePartnerShop()
  const deleteItem = useDeleteRewardCatalogItem()

  // UI state
  const [shopModal, setShopModal] = useState<{ open: boolean; shop?: PartnerShop | null }>({ open: false })
  const [catalogModal, setCatalogModal] = useState<{ open: boolean; item?: RewardCatalogItem | null }>({ open: false })
  const [sessionToken, setSessionToken] = useState<string | null>(null)

  // Reusable confirm-dialog state — the 4 destructive actions below (delete
  // campaign, revoke shop session, delete shop, delete catalog item) each
  // populate this and share ONE rendered ConfirmDialog.
  const [confirmState, setConfirmState] = useState<{
    open: boolean
    title: string
    message: string
    variant: 'error' | 'warning'
    onConfirm: () => void
  }>({ open: false, title: '', message: '', variant: 'error', onConfirm: () => {} })
  const closeConfirm = () => setConfirmState((s) => ({ ...s, open: false }))
  const confirmPending =
    deleteCampaign.isPending || revokeSession.isPending || deleteShop.isPending || deleteItem.isPending

  // Campaign form
  const {
    register: campaignReg,
    handleSubmit: campaignSubmit,
    formState: { errors: campaignErrors, isSubmitting: campaignSaving, isDirty: campaignDirty },
    reset: campaignReset,
  } = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema) as unknown as Resolver<CampaignFormData>,
    defaultValues: {
      name: campaign?.name ?? '',
      description: campaign?.description ?? '',
      earn_model: campaign?.earn_model ?? 'stamp',
      status: campaign?.status ?? 'draft',
      property_id: campaign?.property_id ?? null,
    },
    values: campaign
      ? {
          name: campaign.name,
          description: campaign.description ?? '',
          earn_model: campaign.earn_model,
          status: campaign.status,
          property_id: campaign.property_id,
        }
      : undefined,
  })

  // Publish form
  const {
    register: publishReg,
    handleSubmit: publishSubmit,
    formState: { errors: publishErrors, isSubmitting: publishSaving },
  } = useForm<PublishFormData>({
    resolver: zodResolver(publishSchema) as unknown as Resolver<PublishFormData>,
    defaultValues: {
      funded_amount: 0,
      subsidy_amount: 0,
      starts_at: '',
      ends_at: '',
    },
  })

  const onSaveCampaign = async (data: CampaignFormData) => {
    if (isNew) {
      const created = await createCampaign.mutateAsync(data)
      addToast({ type: 'success', message: t('campaigns.form.create_success') })
      navigate(`/incentives/campaigns/${created.id}`, { replace: true })
    } else {
      await updateCampaign.mutateAsync({ id: campaignId, ...data })
      addToast({ type: 'success', message: t('campaigns.form.update_success') })
      campaignReset(data)
    }
  }

  const onPublish = async (data: PublishFormData) => {
    await publishCampaign.mutateAsync({ id: campaignId, ...data })
    addToast({ type: 'success', message: t('campaigns.form.publish_success') })
  }

  const onDeleteCampaign = () => {
    setConfirmState({
      open: true,
      title: tc('actions.delete'),
      message: t('campaigns.form.delete_confirm'),
      variant: 'error',
      onConfirm: async () => {
        await deleteCampaign.mutateAsync(campaignId)
        addToast({ type: 'success', message: t('campaigns.form.delete_success') })
        closeConfirm()
        navigate('/incentives/campaigns')
      },
    })
  }

  const onRevokeShop = (shopId: number) => {
    setConfirmState({
      open: true,
      title: t('partner_shops.actions.revoke'),
      message: t('partner_shops.actions.revoke_confirm'),
      variant: 'warning',
      onConfirm: async () => {
        await revokeSession.mutateAsync(shopId)
        addToast({ type: 'success', message: t('partner_shops.actions.revoke_success') })
        closeConfirm()
      },
    })
  }

  const onRotateToken = async (shopId: number) => {
    const result = await rotateToken.mutateAsync({ id: shopId })
    setSessionToken(result.session_token)
    addToast({ type: 'success', message: t('partner_shops.actions.rotate_success') })
  }

  const onDeleteShop = (shopId: number) => {
    setConfirmState({
      open: true,
      title: tc('actions.delete'),
      message: t('partner_shops.form.delete_confirm'),
      variant: 'error',
      onConfirm: async () => {
        await deleteShop.mutateAsync(shopId)
        addToast({ type: 'success', message: t('partner_shops.form.delete_success') })
        closeConfirm()
      },
    })
  }

  const onDeleteItem = (itemId: number) => {
    setConfirmState({
      open: true,
      title: tc('actions.delete'),
      message: t('catalog.form.delete_confirm'),
      variant: 'error',
      onConfirm: async () => {
        await deleteItem.mutateAsync(itemId)
        addToast({ type: 'success', message: t('catalog.form.delete_success') })
        closeConfirm()
      },
    })
  }

  const propertyOptions = [
    { value: '', label: tc('actions.select') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const earnModelOptions = [
    { value: 'stamp', label: t('campaigns.earn_model.stamp') },
    { value: 'points', label: t('campaigns.earn_model.points') },
    { value: 'instant', label: t('campaigns.earn_model.instant') },
  ]

  const statusOptions = [
    { value: 'draft', label: t('campaigns.status.draft') },
    { value: 'active', label: t('campaigns.status.active') },
    { value: 'paused', label: t('campaigns.status.paused') },
    { value: 'closed', label: t('campaigns.status.closed') },
  ]

  const shopColumns: Column<PartnerShop>[] = [
    { key: 'name', header: t('partner_shops.fields.name'), render: (s) => s.name },
    { key: 'contact_name', header: t('partner_shops.fields.contact_name'), render: (s) => s.contact_name ?? '—' },
    { key: 'phone', header: t('partner_shops.fields.phone'), render: (s) => s.phone ?? '—', hideBelow: 'md' },
    {
      key: 'devices',
      header: t('partner_shops.fields.devices'),
      render: (s) => (
        <span className={s.has_live_session ? 'text-success font-medium' : 'text-base-content/40'}>
          {s.devices.length}
        </span>
      ),
      hideBelow: 'lg',
    },
    {
      key: 'actions',
      header: '',
      render: (s) => (
        <div className="flex gap-1 justify-end">
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); setShopModal({ open: true, shop: s }) }}
            aria-label={tc('actions.edit')}
          >
            {tc('actions.edit')}
          </Button>
          {s.has_live_session && (
            <Button
              size="xs"
              variant="ghost"
              className="text-warning"
              onClick={(e) => { e.stopPropagation(); onRevokeShop(s.id) }}
              aria-label={t('partner_shops.actions.revoke')}
            >
              <ShieldOff className="w-3 h-3" />
            </Button>
          )}
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onRotateToken(s.id) }}
            aria-label={t('partner_shops.actions.rotate')}
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            className="text-error"
            onClick={(e) => { e.stopPropagation(); onDeleteShop(s.id) }}
            aria-label={tc('actions.delete')}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ]

  const catalogColumns: Column<RewardCatalogItem>[] = [
    { key: 'name', header: t('catalog.fields.name'), render: (i) => i.name },
    {
      key: 'reward_type',
      header: t('catalog.fields.reward_type'),
      render: (i) => t(`catalog.reward_type.${i.reward_type}`),
    },
    {
      key: 'cost_points',
      header: t('catalog.fields.cost_points'),
      render: (i) => i.cost_points,
      hideBelow: 'md',
    },
    {
      key: 'thb_value',
      header: t('catalog.fields.thb_value'),
      render: (i) => formatBaht(i.thb_value),
      hideBelow: 'md',
    },
    {
      key: 'active',
      header: t('catalog.fields.active'),
      render: (i) => (
        <Badge variant={i.active ? 'success' : 'ghost'}>
          {i.active ? tc('status.active') : tc('status.inactive')}
        </Badge>
      ),
      hideBelow: 'lg',
    },
    {
      key: 'actions',
      header: '',
      render: (i) => (
        <div className="flex gap-1 justify-end">
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); setCatalogModal({ open: true, item: i }) }}
          >
            {tc('actions.edit')}
          </Button>
          <Button
            size="xs"
            variant="ghost"
            className="text-error"
            onClick={(e) => { e.stopPropagation(); onDeleteItem(i.id) }}
            aria-label={tc('actions.delete')}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ]

  if (!isNew && isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  const isDraft = !campaign || campaign.status === 'draft'

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('campaigns.title'), href: '/incentives/campaigns' },
          { label: isNew ? t('campaigns.new_button') : (campaign?.name ?? '...') },
        ]}
      />

      <PageHeader
        title={isNew ? t('campaigns.new_button') : (campaign?.name ?? '...')}
        isRefreshing={!isNew && isFetching && !isLoading}
        primaryAction={{
          key: 'save',
          label: isNew ? t('campaigns.form.save_button') : tc('actions.save_changes'),
          onClick: campaignSubmit(onSaveCampaign),
          loading: campaignSaving,
          disabled: !isNew && !campaignDirty,
        }}
        overflowActions={[
          {
            key: 'delete',
            label: tc('actions.delete'),
            icon: Trash2,
            onClick: onDeleteCampaign,
            destructive: true,
            hidden: isNew,
          },
        ]}
      />

      {/* Campaign Form */}
      <Card>
        <form onSubmit={campaignSubmit(onSaveCampaign)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 min-w-0">
              <Input
                label={t('campaigns.fields.name')}
                {...campaignReg('name')}
                error={campaignErrors.name?.message}
              />
            </div>
            <div className="sm:col-span-2 min-w-0">
              <Input
                label={t('campaigns.fields.description')}
                {...campaignReg('description')}
              />
            </div>
            <Select
              label={t('campaigns.fields.earn_model')}
              options={earnModelOptions}
              {...campaignReg('earn_model')}
            />
            <Select
              label={t('campaigns.fields.status')}
              options={statusOptions}
              {...campaignReg('status')}
            />
            <Select
              label={t('campaigns.fields.property')}
              options={propertyOptions}
              {...campaignReg('property_id')}
              className="min-w-0"
            />
          </div>
        </form>
      </Card>

      {/* Publish & Fund panel — only shown for draft campaigns */}
      {!isNew && isDraft && (
        <Card>
          <h2 className="text-lg font-semibold mb-1">{t('campaigns.publish_panel.title')}</h2>
          <p className="text-sm text-base-content/60 mb-4">{t('campaigns.publish_panel.description')}</p>
          <form onSubmit={publishSubmit(onPublish)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('campaigns.publish_panel.funded_amount')}
                type="number"
                min={0}
                step="0.01"
                {...publishReg('funded_amount')}
                error={publishErrors.funded_amount?.message}
              />
              <Input
                label={t('campaigns.publish_panel.subsidy_amount')}
                type="number"
                min={0}
                step="0.01"
                {...publishReg('subsidy_amount')}
                error={publishErrors.subsidy_amount?.message}
              />
              <Input
                label={t('campaigns.publish_panel.starts_at')}
                type="date"
                {...publishReg('starts_at')}
                error={publishErrors.starts_at?.message}
              />
              <Input
                label={t('campaigns.publish_panel.ends_at')}
                type="date"
                {...publishReg('ends_at')}
                error={publishErrors.ends_at?.message}
              />
            </div>
            <Button type="submit" loading={publishSaving}>
              {t('campaigns.publish_panel.publish_button')}
            </Button>
          </form>
        </Card>
      )}

      {/* Pool info — shown when funded */}
      {!isNew && campaign && !isDraft && (
        <Card>
          <p className="text-sm">
            {t('campaigns.publish_panel.pool_info', {
              remaining: formatBaht(campaign.pool_remaining),
              total: formatBaht(campaign.pool_total),
            })}
          </p>
          <div className="mt-2 flex gap-4 text-sm text-base-content/60">
            <span>{t('campaigns.fields.starts_at')}: {campaign.starts_at ? formatDate(campaign.starts_at) : '—'}</span>
            <span>{t('campaigns.fields.ends_at')}: {campaign.ends_at ? formatDate(campaign.ends_at) : '—'}</span>
          </div>
        </Card>
      )}

      {/* Partner Shops subsection */}
      {!isNew && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('partner_shops.title')}</h2>
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShopModal({ open: true, shop: null })}
            >
              {t('partner_shops.new_button')}
            </Button>
          </div>

          {shopsLoading ? (
            <Skeleton className="h-32" />
          ) : !shops.length ? (
            <EmptyState
              title={t('partner_shops.empty.title')}
              description={t('partner_shops.empty.description')}
            />
          ) : (
            <DataTable columns={shopColumns} data={shops} keyExtractor={(s) => s.id} />
          )}
        </Card>
      )}

      {/* Catalog Items subsection */}
      {!isNew && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('catalog.title')}</h2>
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setCatalogModal({ open: true, item: null })}
            >
              {t('catalog.new_button')}
            </Button>
          </div>

          {catalogLoading ? (
            <Skeleton className="h-32" />
          ) : !catalogItems.length ? (
            <EmptyState
              title={t('catalog.empty.title')}
              description={t('catalog.empty.description')}
            />
          ) : (
            <DataTable columns={catalogColumns} data={catalogItems} keyExtractor={(i) => i.id} />
          )}
        </Card>
      )}

      {/* Modals */}
      {shopModal.open && !isNew && (
        <ShopFormModal
          campaignId={campaignId}
          shop={shopModal.shop}
          onClose={() => setShopModal({ open: false })}
        />
      )}

      {catalogModal.open && !isNew && (
        <CatalogItemFormModal
          campaignId={campaignId}
          item={catalogModal.item}
          onClose={() => setCatalogModal({ open: false })}
        />
      )}

      {sessionToken && (
        <TokenRevealPanel
          token={sessionToken}
          onClose={() => setSessionToken(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmState.open}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmPending}
      />
    </div>
  )
}
